import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@repo/ui";
import { authApi } from "~/api";
import type { User } from "~/types/api";
import { usePermissionData } from "./use-permission-data";

type MutationContext = {
  previousPermissions: User[] | undefined;
};

type CustomerMutationVariables = {
  params: {
    path: {
      userId: string;
      customerId: number;
    };
  };
};

type BrandMutationVariables = {
  params: {
    path: {
      userId: string;
      customerId: number;
      brandId: number;
    };
  };
};

type UpdateUserPermissionsReturn = {
  addCustomer: (variables: CustomerMutationVariables) => void;
  removeCustomer: (variables: CustomerMutationVariables) => void;
  isCustomerPending: boolean;

  addBrand: (variables: BrandMutationVariables) => void;
  removeBrand: (userId: string, brandId: number) => void;
  isBrandPending: boolean;

  isCustomerSuccess: boolean;
  isCustomerError: boolean;
  isBrandSuccess: boolean;
  isBrandError: boolean;
};

export const useUpdateUserPermissions = (): UpdateUserPermissionsReturn => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { getCustomerIdForBrand } = usePermissionData();

  const usersQueryOptions = authApi.queryOptions("get", "/api/User");
  const usersQueryKey = usersQueryOptions.queryKey;

  const addCustomerMutation = authApi.useMutation(
    "post",
    "/api/Permission/user/{userId}/customer/{customerId}",
    {
      onMutate: async (variables) => {
        await queryClient.cancelQueries({ queryKey: usersQueryKey });
        const previousPermissions = queryClient.getQueryData<User[]>(usersQueryKey);
        const { userId, customerId } = variables.params.path;

        queryClient.setQueryData<User[]>(usersQueryKey, (oldPermissions) => {
          if (!oldPermissions) return [];
          return oldPermissions.map((user) =>
            user.id === userId && !user.customers.some((c) => c.customerId === customerId)
              ? { ...user, customers: [...user.customers, { userId, customerId }] }
              : user
          );
        });
        return { previousPermissions };
      },
      onError: (_err, _variables, context) => {
        const previousPermissions = (context as MutationContext)?.previousPermissions;
        showToast("error", "Failed to add customer. Rolling back.");
        if (previousPermissions) {
          queryClient.setQueryData(usersQueryKey, previousPermissions);
        }
      },
      onSuccess: () => {
        showToast("success", "Customer added.");
      },
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: usersQueryKey });
      },
    }
  );

  const removeCustomerMutation = authApi.useMutation(
    "delete",
    "/api/Permission/user/{userId}/customer/{customerId}",
    {
      onMutate: async (variables) => {
        await queryClient.cancelQueries({ queryKey: usersQueryKey });
        const previousPermissions = queryClient.getQueryData<User[]>(usersQueryKey);
        const { userId, customerId } = variables.params.path;

        queryClient.setQueryData<User[]>(usersQueryKey, (oldPermissions) => {
          if (!oldPermissions) return [];
          return oldPermissions.map((user) =>
            user.id === userId
              ? {
                  ...user,
                  customers: user.customers.filter((c) => c.customerId !== customerId),
                }
              : user
          );
        });
        return { previousPermissions };
      },
      onError: (_err, _variables, context) => {
        const previousPermissions = (context as MutationContext)?.previousPermissions;
        showToast("error", "Failed to remove customer. Rolling back.");
        if (previousPermissions) {
          queryClient.setQueryData(usersQueryKey, previousPermissions);
        }
      },
      onSuccess: () => {
        showToast("success", "Customer removed.");
      },
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: usersQueryKey });
      },
    }
  );

  const addBrandMutation = authApi.useMutation(
    "post",
    "/api/Permission/user/{userId}/customer/{customerId}/brand/{brandId}",
    {
      onMutate: async (variables) => {
        await queryClient.cancelQueries({ queryKey: usersQueryKey });
        const previousPermissions = queryClient.getQueryData<User[]>(usersQueryKey);
        const { userId, brandId } = variables.params.path;

        queryClient.setQueryData<User[]>(usersQueryKey, (oldPermissions) => {
          if (!oldPermissions) return [];
          return oldPermissions.map((user) =>
            user.id === userId && !user.brands.some((b) => b.brandId === brandId)
              ? { ...user, brands: [...user.brands, { userId, brandId }] }
              : user
          );
        });
        return { previousPermissions };
      },
      onError: (_err, _variables, context) => {
        const previousPermissions = (context as MutationContext)?.previousPermissions;
        showToast("error", "Failed to add brand. Rolling back.");
        if (previousPermissions) {
          queryClient.setQueryData(usersQueryKey, previousPermissions);
        }
      },
      onSuccess: () => {
        showToast("success", "Brand added.");
      },
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: usersQueryKey });
      },
    }
  );

  const removeBrandMutation = authApi.useMutation(
    "delete",
    "/api/Permission/user/{userId}/customer/{customerId}/brand/{brandId}",
    {
      onMutate: async (variables) => {
        await queryClient.cancelQueries({ queryKey: usersQueryKey });
        const previousPermissions = queryClient.getQueryData<User[]>(usersQueryKey);
        const { userId, brandId } = variables.params.path;

        queryClient.setQueryData<User[]>(usersQueryKey, (oldPermissions) => {
          if (!oldPermissions) return [];
          return oldPermissions.map((user) =>
            user.id === userId
              ? {
                  ...user,
                  brands: user.brands.filter((b) => b.brandId !== brandId),
                }
              : user
          );
        });
        return { previousPermissions };
      },
      onError: (_err, _variables, context) => {
        const previousPermissions = (context as MutationContext)?.previousPermissions;
        showToast("error", "Failed to remove brand. Rolling back.");
        if (previousPermissions) {
          queryClient.setQueryData(usersQueryKey, previousPermissions);
        }
      },
      onSuccess: () => {
        showToast("success", "Brand removed.");
      },
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: usersQueryKey });
      },
    }
  );

  return {
    addCustomer: addCustomerMutation.mutate,
    removeCustomer: removeCustomerMutation.mutate,
    isCustomerPending: addCustomerMutation.isPending || removeCustomerMutation.isPending,

    addBrand: addBrandMutation.mutate,
    removeBrand: (userId: string, brandId: number) => {
      const customerId = getCustomerIdForBrand(brandId);
      if (!customerId) {
        showToast("error", "Could not find customerId for brand");
        return;
      }
      removeBrandMutation.mutate({
        params: {
          path: {
            userId,
            brandId,
            customerId,
          },
        },
      });
    },
    isBrandPending: addBrandMutation.isPending || removeBrandMutation.isPending,

    isCustomerSuccess: addCustomerMutation.isSuccess || removeCustomerMutation.isSuccess,
    isCustomerError: addCustomerMutation.isError || removeCustomerMutation.isError,
    isBrandSuccess: addBrandMutation.isSuccess || removeBrandMutation.isSuccess,
    isBrandError: addBrandMutation.isError || removeBrandMutation.isError,
  };
};
